import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-date-range';
import { useHistory } from 'react-router-dom';

import isNumber from 'lodash/isNumber';
import cx from 'classnames';
import moment from 'moment';
import 'moment-timezone';

import { ReactComponent as Clear } from '../../../assets/img/icons/datepicker/Clear.svg';
import { intervalsDate } from '../../../constants/analytics.constants';
import { getRelativeDate } from '../../../utils';
import { getLocalTimestamp } from '../../../utils/commonMethods';
import { deleteSearchParameters, hasTodayQueryParam, setSearchParameters } from '../../../utils/urlFunctions';
import { isSpecificTimeInIntervalRange } from '../../../utils/time';
import CustomTime from './CustomTime';
import CustomSpecificTime from './CustomSpecificTime';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import '../DateTimepicker.scss';

export const CustomCalendar = ({
  selectMax = intervalsDate.length,
  selectMin = 1,
  calendarMax,
  addNotification,
  start,
  end,
  specificTime: specificTimeParams,
  allowFuture,
  timeInterval = null,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  months = 2,
  withTime = false,
  onChange,
  onCancel,
  disableDay,
  singleDay,
  applyOnSelect,
  displayTimeControls = true,
  displayClearOption = true,
}) => {
  const history = useHistory();
  const { startTimeInterval: startTI, endTimeInterval: endTI } = specificTimeParams || {};
  const [calendarKey, setCalendarKey] = useState(0);
  const [time, setTime] = useState(['00:00:00.000', '23:59:59.999']);
  const [specificTime, setSpecificTime] = useState({ start: startTI || '13:00', end: endTI || '15:00' });
  const [showSpecificTime, setShowSpecificTime] = useState(Boolean(startTI || endTI));
  const [activeCalendarItem, setActiveCalendarItem] = useState('');
  const [calendarState, setCalendarState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const daySeconds = 86400;
  let displayTodayOption = true;
  let customSpecificTime;

  useEffect(() => {
    if (timeInterval !== null) {
      const newCalendarState = [
        {
          startDate: new Date(getLocalTimestamp(Date.now(), timezone) - timeInterval.value * 1000),
          endDate: new Date(getLocalTimestamp(Date.now(), timezone)),
          key: 'selection',
        },
      ];

      setCalendarState(newCalendarState);
      if (withTime) {
        const newTime = [
          moment(getLocalTimestamp(Date.now(), timezone) - timeInterval.value * 1000).format('HH:mm:ss.SSS'),
          moment(getLocalTimestamp(Date.now(), timezone)).format('HH:mm:ss.SSS'),
        ];

        setTime(newTime);
      }
      setActiveCalendarItem(timeInterval.icon);
    } else if (start !== null && start !== 'select' && end !== null) {
      const newCalendarState = [
        {
          startDate: new Date(start),
          endDate: new Date(end),
          key: 'selection',
        },
      ];

      setCalendarState(newCalendarState);

      if (withTime) {
        const newTime = [
          moment(start).format('HH:mm:ss.SSS'),
          moment(
            moment(end).isSame(moment().tz(timezone), 'day') && timeInterval !== null
              ? new Date(getLocalTimestamp(Date.now(), timezone))
              : new Date(end)
          ).format('HH:mm:ss.SSS'),
        ];

        setTime(newTime);
      }

      if (hasTodayQueryParam()) {
        setActiveCalendarItem('today');
      } else {
        setActiveCalendarItem('custom');
      }
    } else if (withTime) {
      const newTime = [time[0], moment(getLocalTimestamp(Date.now(), timezone)).format('HH:mm:ss.SSS')];
      setTime(newTime);
    }
  
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', checkCalendarClick);
    }, 100);
  
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', checkCalendarClick);
    };
  }, []);

  const onChangeAction = (startDate, endDate = null) => {
    onChange(startDate, endDate);

    if (specificTimeParams) {
      if (startDate === 'select' && endDate === 'select') {
        resetSpecificTime();
      } else {
        applySpecificTime();
      }
    }
  };

  function checkCalendarClick(e) {
    if (
      !(e.path || (e.composedPath && e.composedPath())).includes(
        document.getElementById('datepicker_custom_range__container')
      )
    ) {
      onCancel();
    }
  }
  
  const resetSpecificTime = () => {
    history.push(deleteSearchParameters(['startTimeInterval', 'endTimeInterval']));
    specificTimeParams?.onApply(null, null);
  };

  const applySpecificTime = () => {
    if (showSpecificTime) {
      // TODO
      const momentStart = moment(`2024-01-01 ${specificTime.start}:00`);
      const momentEnd = moment(`2024-01-01 ${specificTime.end}:00`);
      const isValidTimeRange = momentEnd.isSameOrAfter(momentStart, 'minute');

      if (isValidTimeRange) {
        history.push(
          setSearchParameters(
            ['startTimeInterval', 'endTimeInterval'],
            [specificTime.start, specificTime.end]
          )
        );

        specificTimeParams.onApply(specificTime.start, specificTime.end);
      }
    } else if (startTI || endTI) {
      resetSpecificTime();
    }
  };

  const applyDates = () => {
    if (withTime) {
      onChangeAction(
        new Date(moment(calendarState[0].startDate).format('YYYY-MM-DD') + 'T' + time[0]).getTime(),
        new Date(moment(calendarState[0].endDate).format('YYYY-MM-DD') + 'T' + time[1]).getTime()
      );
    } else {
      onChangeAction(calendarState[0].startDate, calendarState[0].endDate);
    }
  };

  const isRangeExceedMaxValue = () => {
    if (
      calendarMax !== null &&
      calendarState[0].endDate.getTime() - calendarState[0].startDate.getTime() > calendarMax
    ) {
      addNotification(
        'danger',
        `Date range must be
          less than ${getRelativeDate(Date.now() - calendarMax)}`.replace('ago', ''),
        'Error',
        3000
      );
      
      return true;
    }
    
    return false;
  };
  
  const isValidRange = () => {
    if (withTime) {
      const startTs = new Date(moment(calendarState[0].startDate).format('YYYY-MM-DD') + 'T' + time[0]).getTime();
      const endTs = new Date(moment(calendarState[0].endDate).format('YYYY-MM-DD') + 'T' + time[1]).getTime();
      
      return endTs > startTs;
    } else {
      const startMoment = moment(calendarState[0].startDate);
      const endMoment = moment(calendarState[0].endDate);
  
      return endMoment.isSameOrAfter(startMoment);
    }
  };

  const getSeconds = (timeString, format) => {
    return moment.duration(timeString, format).asSeconds();
  };

  const makeDateTimeMoment = (dateVal, timeVal) => {
    return moment(moment(dateVal).format('YYYY-MM-DD') + 'T' + timeVal);
  };

  const updateSpecificTime = (newCalendarState, newTime) => {
    const newMomentDateTimeStart = makeDateTimeMoment(newCalendarState[0].startDate, newTime[0]);
    const newMomentDateTimeEnd = makeDateTimeMoment(newCalendarState[0].endDate, newTime[1]);
    const diffSeconds = newMomentDateTimeEnd.diff(newMomentDateTimeStart, 'seconds');
    const isRangeIn24Hours = Math.abs(diffSeconds) <= daySeconds;
    let newSpecificTime;

    if (isRangeIn24Hours) {
      const specificTimeDiffSec = 7200;
      const timeFormat = 'HH:mm:ss.SSS';
      const specificTimeFormat = 'HH:mm';
      const newSpecificTimeRange = [0, 0];
      const timeRange = [getSeconds(newTime[0], timeFormat), getSeconds(newTime[1], timeFormat)];

      const specificTimeRange = [
        getSeconds(specificTime.start, specificTimeFormat),
        getSeconds(specificTime.end, specificTimeFormat),
      ];

      const isStartTimeInRange = (range) => {
        return range[0] <= specificTimeRange[0] && specificTimeRange[0] <= range[1];
      };

      const isEndTimeInRange = () => {
        return newSpecificTimeRange[0] <= specificTimeRange[1] && specificTimeRange[1] <= timeRange[1];
      };

      const makeSpecificTime = (seconds) => {
        return moment().startOf('day').add(seconds, 'seconds').format('HH:mm');
      };

      if (!newMomentDateTimeStart.isSame(newMomentDateTimeEnd, 'day')) {
        if (isStartTimeInRange([timeRange[0], 86399])) {
          newSpecificTimeRange[0] = specificTimeRange[0];
          timeRange[1] = 86399;
        } else if (isStartTimeInRange([0, timeRange[1]])) {
          newSpecificTimeRange[0] = specificTimeRange[0];
          timeRange[0] = 0;
        } else {
          newSpecificTimeRange[0] = timeRange[0];
          timeRange[1] = 86399;
        }
      } else {
        if (isStartTimeInRange(timeRange)) {
          newSpecificTimeRange[0] = specificTimeRange[0];
        } else {
          newSpecificTimeRange[0] = timeRange[0];
        }
      }

      if (isEndTimeInRange()) {
        newSpecificTimeRange[1] = specificTimeRange[1];
      } else {
        const diffSeconds = timeRange[1] - newSpecificTimeRange[0];

        if (diffSeconds >= specificTimeDiffSec) {
          newSpecificTimeRange[1] = newSpecificTimeRange[0] + specificTimeDiffSec;
        } else {
          newSpecificTimeRange[1] = timeRange[1];
        }
      }

      newSpecificTime = {
        start: makeSpecificTime(newSpecificTimeRange[0]),
        end: makeSpecificTime(newSpecificTimeRange[1]),
      };
    }

    if (
      newSpecificTime &&
      (specificTime.start !== newSpecificTime.start || specificTime.end !== newSpecificTime.end)
    ) {
      setSpecificTime(newSpecificTime);
    }
  };

  const changeCalendarState = (item) => {
    const endDate = singleDay ? item.startDate : item.endDate;
    
    const newCalendarState = [
      {
        startDate: item.startDate,
        endDate,
        key: item.key,
      },
    ];

    setCalendarState(newCalendarState);

    if (
      item.endDate.toDateString() === new Date().toDateString() &&
      typeof intervalsDate.find(
        (interval) => interval.value === moment(item.endDate).diff(moment(item.startDate), 'seconds')
      ) !== 'undefined'
    ) {
      setActiveCalendarItem(
        intervalsDate.find(
          (interval) => interval.value === moment(item.endDate).diff(moment(item.startDate), 'seconds')
        ).icon
      );
    } else {
      setActiveCalendarItem('custom');
    }

    const newTime = [
      moment(item.startDate).format('HH:mm:ss.SSS'),
      item.endDate.toDateString() === new Date().toDateString()
        ? moment().tz(timezone).format('HH:mm:ss.SSS')
        : '23:59:59.999',
    ];

    setTime(newTime);

    if (showSpecificTime) {
      updateSpecificTime(newCalendarState, newTime);
    }
  
    if (applyOnSelect) {
      if (withTime) {
        onChangeAction(
          new Date(moment(newCalendarState[0].startDate).format('YYYY-MM-DD') + 'T' + newTime[0]).getTime(),
          new Date(moment(newCalendarState[0].endDate).format('YYYY-MM-DD') + 'T' + newTime[1]).getTime()
        );
      } else {
        onChangeAction(newCalendarState[0].startDate, newCalendarState[0].endDate);
      }
    }
  };

  const onChangeTime = (newTime) => {
    setTime(newTime);

    if (showSpecificTime) {
      updateSpecificTime(calendarState, newTime);
    }
  };

  const onChangeShowSpecificTime = (newShowSpecificTime) => {
    setShowSpecificTime(newShowSpecificTime);

    if (newShowSpecificTime) {
      updateSpecificTime(calendarState, time);
    }
  };

  const setInitialCalendarState = () => {
    setActiveCalendarItem('');
    setCalendarState([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      },
    ]);
    onCancel();
  };

  if (specificTimeParams) {
    const todayEndTimeSeconds = getSeconds(moment.tz(moment(), timezone).format('HH:mm'));
    const newMomentDateTimeStart = makeDateTimeMoment(calendarState[0].startDate, time[0]);
    const newMomentDateTimeEnd = makeDateTimeMoment(calendarState[0].endDate, time[1]);
    const diffSeconds = newMomentDateTimeEnd.diff(newMomentDateTimeStart, 'seconds');
    const isRangeIn24Hours = Math.abs(diffSeconds) <= daySeconds;
    let minSpecificTimeTime;
    let maxSpecificTimeTime;
    let excludeRange;

    if (isRangeIn24Hours) {
      if (!newMomentDateTimeStart.isSame(newMomentDateTimeEnd, 'day')) {
        excludeRange = [getSeconds(time[1]) + 1, getSeconds(time[0]) - 1];
      } else {
        minSpecificTimeTime = time[0];
        maxSpecificTimeTime = time[1];
      }
    }

    if (showSpecificTime && getSeconds(specificTime.end) > todayEndTimeSeconds) {
      displayTodayOption = false;
    }

    customSpecificTime = (
      <CustomSpecificTime
        specificTime={specificTime}
        setSpecificTime={setSpecificTime}
        showSpecificTime={showSpecificTime}
        onChangeShowSpecificTime={onChangeShowSpecificTime}
        minTime={minSpecificTimeTime}
        maxTime={maxSpecificTimeTime}
        excludeRange={excludeRange}
      />
    );
  }

  return (
    <div className="datepicker-custom-range" id="datepicker_custom_range__container">
      <div
        className={cx('datepicker-custom-range__body', {
          'datepicker-custom-range__body-has-time': withTime,
          'datepicker-custom-range__body-has-specific-time': !!specificTimeParams,
          'datepicker-custom-range__body-has-specific-time-controls': showSpecificTime,
          'datepicker-custom-range__body-no-footer': applyOnSelect && !displayTimeControls,
        })}
      >
        <ul className="datepicker-ranges datepicker-ranges__scrolled">
          {intervalsDate.slice(selectMin, selectMax).map((item, i) => {
            if (
              showSpecificTime &&
              isNumber(item.value) &&
              item.value < daySeconds &&
              !isSpecificTimeInIntervalRange(specificTime, timezone, item.value)
            ) {
              return null;
            }

            return (
              <li
                key={i}
                className={cx('datepicker-ranges-item', {
                  'datepicker-ranges-item__active': activeCalendarItem === item.icon,
                })}
                onClick={() => onChangeAction(item.icon)}
              >
                {item.label}
              </li>
            );
          })}
          
          {displayTodayOption ? (
            <li
              className={cx('datepicker-ranges-item', {
                'datepicker-ranges-item__active': activeCalendarItem === 'today',
              })}
              onClick={() => {
                onChangeAction('today', 'today');
              }}
            >
              Today
            </li>
          ) : null}
          
          <li
            className={`datepicker-ranges-item datepicker-ranges-item__disabled ${
              activeCalendarItem === 'custom' ? 'datepicker-ranges-item__active' : ''
            }`}
          >
            Custom
          </li>
          
          {displayClearOption ? (
            <li
              className="datepicker-ranges-item datepicker-ranges-item__clear"
              onClick={() => {
                onChangeAction('select', 'select');
              }}
            >
              <Clear />
              Clear
            </li>
          ) : null}
        </ul>

        <div className="datepicker-custom-range__calendar-container">
          <DateRange
            key={calendarKey}
            className="datepicker-custom-range__calendar"
            maxDate={allowFuture ? undefined : new Date()}
            minDate={new Date(2019, 0, 0, 0)}
            ranges={calendarState}
            fixedHeight={true}
            onChange={(item) => {
              if (singleDay) {
                changeCalendarState({
                  ...item.selection,
                  endDate: item.selection.startDate,
                });
    
                setCalendarKey(val => val + 1);
    
                return;
              }
              
              changeCalendarState(item.selection);
            }}
            showMonthArrow={true}
            showMonthAndYearPickers={false}
            showDateDisplay={false}
            direction="horizontal"
            calendarFocus="backwards"
            preventSnapRefocus={true}
            months={months}
            rangeColors={['#dd00311a', '#3ecf8e', '#fed14c']}
            disabledDay={disableDay}
          />

          {displayTimeControls && (withTime || customSpecificTime) ? (
            <div className="datepicker-custom-range__calendar-footer">
              {withTime ? (
                <CustomTime
                  calendarState={calendarState}
                  time={time}
                  onChange={onChangeTime}
                  timezone={timezone}
                />
              ) : null}

              {customSpecificTime}
            </div>
          ) : null}
        </div>
      </div>

      {!applyOnSelect ? (
        <div className="datepicker-custom-range__footer">
          <div className="datepicker-custom-range__footer__buttons">
            <button
              className="datepicker-custom-range__footer__buttons-item datepicker-custom-range__footer__buttons-cancel"
              onClick={() => {
                setInitialCalendarState();
              }}
            >
              Cancel
            </button>
            <button
              className={cx('datepicker-custom-range__footer__buttons-item datepicker-custom-range__footer__buttons-apply', {
                disabled: isRangeExceedMaxValue() || !isValidRange(),
              })}
              onClick={applyDates}
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
